import React, { useState, useEffect } from "react";
import { Form, Button, Row, Col, Spinner } from "react-bootstrap";
import SchemeSearch from "./searchMedicalAid";
import PlanOptionSelector from "./searchMedicalAidOptionPlan";
import { checkFamilyEligibility } from "../../mediKreditapi";
import { jwtDecode } from "jwt-decode";
import { getUserPracticeById } from "../../api";

export default function SearchMedAidPatient({
  onDependantSelect,
  addPatientSteps,
  patientAccount,
  accountExisting,
  medicalAidMainMember,
  onDependantListUpdate,
}) {
  // ✅ If account exists, use its medical aid details. Otherwise, allow manual input.
  const [mainMemberData, setMainMemberData] = useState(null);
  const [medicalAidInfo, setMedicalAidInfo] = useState({
    medicalAid: accountExisting ? patientAccount.accountMedicalAid || "" : "",
    medicalAidCode: accountExisting
      ? patientAccount.accountMedicalAidCode || ""
      : "",
    medicalAidPlan: accountExisting
      ? patientAccount.accountMedicalAidPlan || ""
      : "",
    medicalAidPlanCode: accountExisting
      ? patientAccount.accountMedicalAidPlanCode || ""
      : "",
    medicalAidOption: accountExisting
      ? patientAccount.accountMedicalAidOption || ""
      : "",
    medicalAidOptionCode: accountExisting
      ? patientAccount.accountMedicalAidOptionCode || ""
      : "",
    plan: accountExisting
      ? patientAccount.accountMediKreditPlanNumber03 ||
        patientAccount.accountMediKreditPlanNumber04 ||
        patientAccount.accountMediKreditPlanNumber06 ||
        ""
      : "", // Default to empty string if no plan
    mediKreditPlanNumber03: accountExisting
      ? patientAccount.accountMediKreditPlanNumber03 || ""
      : "",
    mediKreditPlanNumber04: accountExisting
      ? patientAccount.accountMediKreditPlanNumber04 || ""
      : "",
    mediKreditPlanNumber06: accountExisting
      ? patientAccount.accountMediKreditPlanNumber06 || ""
      : "",
    ch_id: accountExisting ? patientAccount.accountMedicalAidNumber || "" : "",
    sname: accountExisting ? patientAccount.accountLastName || "" : "",
    inits: accountExisting ? patientAccount.accountInitials || "" : "",
    lastCheck: accountExisting ? patientAccount.accountLastCheck || "" : "",
    errorMessage: false,
  });

  const [dependants, setDependants] = useState([]);
  const [selectedDependant, setSelectedDependant] = useState(null);
  const [errorMesTrigger, setErrorMesTrigger] = useState(false);
  const [loading, setLoading] = useState(false); // ✅ Loading state for the button
  const [statusInfo, setStatusInfo] = useState();
  const [practice, setPractice] = useState(null); // ✅ State for practice
  const [practiceInfo, setPracticeInfo] = useState({
    sp_bhf: "",
    sp_hpc: "",
    grp_prac: "",
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const token = sessionStorage.getItem("user");
        if (!token) throw new Error("No user token found");

        const user = jwtDecode(token);
        const practices = await getUserPracticeById(user._id);
        setPractice(practices[0]); // ✅ Set practice from user data
        setPracticeInfo({
          sp_bhf: practices[0].practiceNumber,
          sp_hpc: practices[0].hpcsaNumber,
          grp_prac: practices[0].practiceNumber,
        });
      } catch (error) {
        console.error("❌ Error loading Ozow data:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  // ✅ Only allow manual input if no existing account
  const handleSchemeSelect = (scheme) => {
    if (!accountExisting) {
      setMedicalAidInfo((prev) => ({
        ...prev,
        medicalAid: scheme.name,
        medicalAidCode: scheme.code,
      }));
    }
  };

  const handlePlanOptionSelect = (selectedPlanData) => {
    if (!accountExisting && selectedPlanData) {
      const extractDigits = (value) => value?.match(/\d{6}$/)?.[0] || ""; // Extracts 6 trailing digits

      setMedicalAidInfo((prev) => ({
        ...prev,
        medicalAidPlan: selectedPlanData.patientMedicalAidPlan || "",
        medicalAidPlanCode: selectedPlanData.patientMedicalAidPlanCode || "",
        medicalAidOption: selectedPlanData.patientMedicalAidOption || "",
        medicalAidOptionCode:
          selectedPlanData.patientMedicalAidOptionCode || "",
        mediKreditPlanNumber03: extractDigits(
          selectedPlanData.patientMediKreditPlanNumber03
        ),
        mediKreditPlanNumber04: extractDigits(
          selectedPlanData.patientMediKreditPlanNumber04
        ),
        mediKreditPlanNumber06: extractDigits(
          selectedPlanData.patientMediKreditPlanNumber06
        ),
        plan: extractDigits(
          selectedPlanData.patientMediKreditPlanNumber03 ||
            selectedPlanData.patientMediKreditPlanNumber04 ||
            selectedPlanData.patientMediKreditPlanNumber06 ||
            ""
        ),
      }));
    }
    console.log("✅ Plan Option Selected:", selectedPlanData);
  };

  const handleChangeMedicalAid = (e) => {
    if (!accountExisting) {
      const { name, value } = e.target;
      setMedicalAidInfo((prev) => ({
        ...prev,
        [name]: value,
        lastCheck: new Date().toISOString(),
      }));
    }
  };

  // ✅ Fetch dependants based on provided medical aid details
  async function famCheck(e) {
    setLoading(true);
    console.log("famcheck medicalAidInfo", medicalAidInfo);
    try {
      const response = await checkFamilyEligibility(
        practiceInfo,
        medicalAidInfo
      );
      console.log("response", response);
      // ✅ Update status
      setStatusInfo({
        auths: response.auths || null,
        txNumber: response.txNumber || null,
        resultCode: response.resultCode || null,
        warnings: response.warnings || [],
        rejections: response.rejections || [],
        dependantPlanDescription: response.dependantPlanDescription || null,
      });

      if (response.dependants && response.dependants.length > 0) {
        const dependantsList = response.dependants;

        let mainMember = dependantsList.find((dep) =>
          ["0", "00", "000"].includes(dep.dependantCode)
        );
        if (!mainMember) {
          mainMember = dependantsList.find((dep) =>
            ["1", "01", "001"].includes(dep.dependantCode)
          );
        }

        setDependants(dependantsList);
        setSelectedDependant(null);

        if (typeof onDependantListUpdate === "function") {
          onDependantListUpdate(dependantsList);
        }

        if (mainMember && typeof medicalAidMainMember === "function") {
          const mainData = {
            ...mainMember,
            medicalAidInfo,
          };
          setMainMemberData(mainData); // ✅ save to local state
          medicalAidMainMember(mainData); // ✅ optional: pass back to parent

          console.log("✅ Updated Main Member Data!:", mainMemberData);
        }
      } else {
        setDependants([]);
        setSelectedDependant(null);
      }
    } catch (error) {
      console.error("⛔ Family Check Error:", error);

      if (
        error.response &&
        (error.response.status === 404 || error.response.status === 503)
      ) {
        const data = error.response.data || {};

        setStatusInfo({
          auths: data.auths || null,
          txNumber: data.txNumber || null,
          resultCode: data.resultCode || null,
          warnings: data.warnings || [],
          rejections: data.rejections || [],
        });

        const updatedMedicalAidInfo = {
          ...medicalAidInfo,
          errorMessage: true,
          lastCheck: new Date().toISOString(),
        };
        setErrorMesTrigger(true);
        setMedicalAidInfo(updatedMedicalAidInfo);

        if (typeof addPatientSteps === "function") {
          addPatientSteps({ ...updatedMedicalAidInfo });
        }
      }
    } finally {
      setLoading(false); // ✅ Reset loading state
    }
  }

  useEffect(() => {
    if (errorMesTrigger) {
      setMedicalAidInfo((prev) => ({
        ...prev,
        errorMessage: true,
        lastCheck: new Date().toISOString(),
      }));
    }
    console.log("useEffect triggered", medicalAidInfo);
  }, [errorMesTrigger]);

  // ✅ Pass selected dependant & medical aid info to addPatientSteps
  useEffect(() => {
    if (selectedDependant) {
      console.log("Selected Dependant:", selectedDependant);
      setMedicalAidInfo((prev) => ({
        ...prev,
        lastCheck: new Date().toISOString(),
        hnetNumber: selectedDependant.hnetNumber || "",
        txNumber: selectedDependant.txNumber || "",
      }));
      const patientData = {
        ...selectedDependant,
        medicalAidInfo,
      };
      addPatientSteps(patientData, mainMemberData); // ✅ FIXED
      console.log("✅ Updated Patient Data:", patientData);
      console.log("Mian Member Data:", medicalAidMainMember);
    }
  }, [selectedDependant]);

  return (
    <>
      {/* ✅ Show Medical Aid Fields Only for Non-Existing Accounts */}
      {!accountExisting && (
        <>
          <Row className="mb-1">
            <Col md={6}>
              <Form.Group controlId="formGridMedicalAid">
                <Form.Label>Medical Aid</Form.Label>
                <SchemeSearch onSchemeCodeSelect={handleSchemeSelect} />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="formGridMedicalAidPlan">
                <PlanOptionSelector
                  schemeCode={medicalAidInfo.medicalAidCode}
                  onPlanOptionSelect={handlePlanOptionSelect}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={4}>
              <Form.Group controlId="formGridMedicalAidNumber">
                <Form.Label>Medical Aid Number</Form.Label>
                <Form.Control
                  required
                  value={medicalAidInfo.ch_id}
                  name="ch_id"
                  onChange={handleChangeMedicalAid}
                />
              </Form.Group>
            </Col>
            <Col md={5}>
              <Form.Group controlId="formGridLastName">
                <Form.Label>Main Member Last Name</Form.Label>
                <Form.Control
                  required
                  name="sname"
                  value={medicalAidInfo.sname}
                  onChange={handleChangeMedicalAid}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="formGridInitials">
                <Form.Label>Main Member Initials</Form.Label>
                <Form.Control
                  name="inits"
                  value={medicalAidInfo.inits}
                  onChange={handleChangeMedicalAid}
                />
              </Form.Group>
            </Col>
          </Row>
        </>
      )}

      {/* ✅ Search Dependants Button */}
      <Row className="mb-3">
        <Button
          className="w-25 ms-3 d-flex align-items-center justify-content-center gap-2"
          onClick={famCheck}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />{" "}
              Loading...
            </>
          ) : (
            <>
              <i className="fa-solid fa-magnifying-glass"></i>
              Search
            </>
          )}
        </Button>
      </Row>

      {/* ✅ Display Dependants If Found */}
      {(dependants.length > 0 || statusInfo) && (
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group controlId="formGridDependants">
              <Form.Label>Select Dependant</Form.Label>
              <Form.Select
                value={selectedDependant?.dependantCode || ""}
                onChange={(e) => {
                  const selectedCode = e.target.value;
                  const dependant = dependants.find(
                    (d) => d.dependantCode === selectedCode
                  );

                  if (dependant) {
                    const patientData = {
                      ...dependant,
                      medicalAidInfo,
                    };

                    setSelectedDependant(patientData);
                    addPatientSteps(patientData, mainMemberData);
                    if (typeof onDependantSelect === "function") {
                      onDependantSelect(patientData);
                    }
                  }
                }}
              >
                <option value="" disabled hidden>
                  Select a dependant
                </option>
                {dependants.map((dep) => (
                  <option key={dep.dependantCode} value={dep.dependantCode}>
                    {dep.dependantCode} - {dep.dependantFirstName}{" "}
                    {dep.dependantLastName}
                    {/* ✅ Add plan description to dropdown */}
                    {dep.dependantPlanDescription &&
                      ` (${dep.dependantPlanDescription})`}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          {/* ✅ Display Status Info */}
          <Col md={6}>
            {statusInfo ? (
              <>
                <div
                  className={
                    statusInfo?.resultCode === "A"
                      ? "text-success fw-bold"
                      : "text-danger fw-bold"
                  }
                >
                  {statusInfo?.resultCode === "A" ? "Success" : "Unsuccessful"}
                </div>

                {Array.isArray(statusInfo?.warnings) &&
                  statusInfo?.warnings.map((warn, index) => (
                    <p className="mb-0" key={`warn-${index}`}>
                      {warn.code} - {warn.description}
                    </p>
                  ))}

                {Array.isArray(statusInfo?.rejections) &&
                  statusInfo.rejections.map((rej, index) => (
                    <p className="mb-0 text-danger" key={`rej-${index}`}>
                      {rej.code} - {rej.description}
                    </p>
                  ))}

                {/* ✅ Show main member plan at top level */}
                <div>
                  <strong>Main Member Plan:</strong>{" "}
                  {statusInfo.dependantPlanDescription || "N/A"}
                </div>

                <div>
                  <strong>HNET:</strong> {statusInfo.auths || "N/A"}
                </div>

                <div>
                  <strong>TX Number:</strong> {statusInfo.txNumber || "N/A"}
                </div>

                {/* ✅ Show selected dependant's specific plan */}
                {selectedDependant && (
                  <div className="mt-2 pt-2 border-top">
                    <strong>Selected Dependant Plan:</strong>{" "}
                    {selectedDependant.dependantPlanDescription || "N/A"}
                  </div>
                )}
              </>
            ) : (
              <div>No status yet.</div>
            )}
          </Col>
        </Row>
      )}
    </>
  );
}
